<template>
  <div
    v-if="variant === 'payment'"
    class="payment-button-wrapper"
  >
    <div
      ref="confettiContainer"
      class="confetti-container"
    ></div>

    <v-btn
      ref="paymentButton"
      :class="paymentButtonClasses"
      :disabled="disabled || loading || paymentAnimating || paymentSuccess"
      :loading="paymentAnimating && !paymentSuccess"
      text
      v-bind="$attrs"
      @click="handlePaymentClick"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <span
          v-if="!paymentAnimating && !paymentSuccess"
          key="initial"
          class="button-content payment-content"
        >
          <slot>Processar Pagamento</slot>
        </span>

        <span
          v-else-if="paymentAnimating && !paymentSuccess"
          key="processing"
          class="button-content payment-content"
        >
          Processando...
        </span>

        <span
          v-else
          key="success"
          class="button-content payment-content success-content"
        >
          <PhCheckCircle
            :size="20"
            weight="fill"
            class="mr-1"
          />
          <span>Pago!</span>
        </span>
      </transition>
    </v-btn>
  </div>

  <v-btn
    v-else
    :class="buttonClasses"
    :disabled="disabled || (loading && !keepColorWhileLoading)"
    :loading="loading"
    :block="block"
    :text="isTextButton"
    :icon="iconOnly"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <slot></slot>
  </v-btn>
</template>

<script>
export default {
  name: 'Button',
  inheritAttrs: false,
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return [
          'primary',
          'secondary',
          'tertiary',
          'warning',
          'success',
          'destructive',
          'actions',
          'payment',
          'tonal'
        ].includes(value);
      }
    },
    tonalColor: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return ['primary', 'success', 'warning', 'error', 'black'].includes(value);
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
    // Carregando sem ficar cinza: mantém a cor e só bloqueia o clique (telas do cliente)
    keepColorWhileLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      paymentAnimating: false,
      paymentSuccess: false
    };
  },
  computed: {
    buttonClasses() {
      const classes = [];

      if (this.variant === 'actions') {
        classes.push('actions-btn');
      } else if (this.variant === 'tonal') {
        classes.push('btn-tonal');
        classes.push(`btn-tonal-${this.tonalColor}`);
      } else if (this.variant !== 'payment') {
        classes.push(`btn-${this.variant}`);
      }

      if (this.block) {
        classes.push('btn-block');
      }

      if (this.loading && this.keepColorWhileLoading) {
        classes.push('btn-loading-readonly');
      }

      if (this.iconOnly) {
        classes.push('btn-icon-only');
      }

      return classes;
    },
    isTextButton() {
      return this.variant === 'primary' || this.variant === 'secondary';
    },
    paymentButtonClasses() {
      const classes = ['payment-button'];

      if (this.paymentSuccess) {
        classes.push('btn-success', 'success');
      } else {
        classes.push('btn-primary');
      }

      if (this.paymentAnimating) {
        classes.push('animating');
      }

      return classes;
    }
  },
  methods: {
    async handlePaymentClick() {
      this.paymentAnimating = true;
      this.paymentSuccess = false;

      await new Promise((resolve) => setTimeout(resolve, 2000));

      this.paymentSuccess = true;
      this.paymentAnimating = false;

      await this.$nextTick();

      this.createConfetti();

      this.$emit('payment-success');
    },
    createConfetti() {
      const colors = ['#73BC49'];
      const confettiContainer = this.$refs.confettiContainer;
      const button = this.$refs.paymentButton.$el;

      if (!confettiContainer || !button) return;

      const buttonRect = button.getBoundingClientRect();
      const centerX = buttonRect.left + buttonRect.width / 2;
      const centerY = buttonRect.top + buttonRect.height / 2;

      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        const angle = (Math.random() * 360 * Math.PI) / 180;
        const velocity = 150 + Math.random() * 150;
        const tx = Math.cos(angle) * velocity;
        const ty = Math.sin(angle) * velocity + 100;

        confetti.style.cssText = `
          position: fixed;
          width: ${6 + Math.random() * 10}px;
          height: ${6 + Math.random() * 10}px;
          background-color: ${colors[Math.floor(Math.random() * colors.length)]};
          left: ${centerX}px;
          top: ${centerY}px;
          border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
          animation: confetti-burst-${i} 1.5s ease-out forwards;
          pointer-events: none;
          z-index: 10000;
        `;

        const keyframes = `
          @keyframes confetti-burst-${i} {
            0% {
              transform: translate(0, 0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translate(${tx}px, ${ty - 300}px) rotate(${720 + Math.random() * 360}deg);
              opacity: 0;
            }
          }
        `;

        const style = document.createElement('style');
        style.textContent = keyframes;
        document.head.appendChild(style);

        confettiContainer.appendChild(confetti);

        setTimeout(() => {
          confetti.remove();
          style.remove();
        }, 1600);
      }
    }
  }
};
</script>

<style scoped>
/* Os estilos são herdados do arquivo _buttons.scss global */

/* Carregando sem desabilitar: mesma cor, sem clique */
.btn-loading-readonly {
  pointer-events: none;
}

.payment-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
}

.success-content {
  display: inline-flex;
  align-items: center;
}
</style>

